<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { DollarSign } from "@lucide/svelte"

  import AvailableTaskTemplateCard from "@/components/task/available-task-template-card.svelte"
  import TaskCard from "@/components/task/task-card.svelte"
  import TaskList from "@/components/task/task-list.svelte"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import {
    claimTaskInstancesPath,
    completeTaskInstancePath,
    userDashboardPath,
    userDashboardTaskHistoryPath,
  } from "@/routes"
  import type { BreadcrumbItem, TaskInstance, TaskTemplate } from "@/types"
  import { subscribeToUserDashboardChannel } from "@/utils/actioncable"

  interface Props {
    assignedTasks: TaskInstance[]
    availableTaskTemplates: TaskTemplate[]
    recentTasks: TaskInstance[]
    wageSummary: {
      earnedThisWeek: number
      paidLast30Days: number
    }
  }

  let {
    assignedTasks,
    availableTaskTemplates,
    recentTasks,
    wageSummary,
  }: Props = $props()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Dashboard",
      href: userDashboardPath(),
    },
  ]

  $effect(() => {
    const subscription = subscribeToUserDashboardChannel((data) => {
      console.log("User dashboard update received:", data)
    })

    return () => {
      subscription.unsubscribe()
    }
  })

  let completeDialogOpen = $state(false)
  let claimDialogOpen = $state(false)
  let cancelDialogOpen = $state(false)
  let pendingCompleteTask: TaskInstance | null = $state(null)
  let pendingClaimTemplate: TaskTemplate | null = $state(null)
  let pendingCancelTaskId: number | null = $state(null)

  function handleComplete(taskInstance: TaskInstance) {
    pendingCompleteTask = taskInstance
    completeDialogOpen = true
  }

  function confirmComplete() {
    if (pendingCompleteTask) {
      router.post(
        completeTaskInstancePath(pendingCompleteTask.id),
        {},
        {
          preserveScroll: true,
        },
      )
    }
  }

  function handleClaim(taskTemplate: TaskTemplate) {
    pendingClaimTemplate = taskTemplate
    claimDialogOpen = true
  }

  function confirmClaim() {
    if (pendingClaimTemplate) {
      router.post(
        claimTaskInstancesPath(),
        { task_template_id: pendingClaimTemplate.id },
        {
          preserveScroll: true,
        },
      )
    }
  }

  function handleCancel(taskInstanceId: number) {
    pendingCancelTaskId = taskInstanceId
    cancelDialogOpen = true
  }

  function confirmCancel() {
    if (pendingCancelTaskId !== null) {
      router.delete(`/task_instances/${pendingCancelTaskId}`, {
        preserveScroll: true,
      })
    }
  }
</script>

<svelte:head>
  <title>Dashboard - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div>
      <h1 class="text-3xl font-bold">My Dashboard</h1>
      <p class="text-muted-foreground mt-2">View your tasks and earnings</p>
    </div>

    <TaskList
      tasks={assignedTasks}
      title="Assigned Tasks"
      showActions={true}
      onComplete={handleComplete}
      onCancel={handleCancel}
    />
    {#if availableTaskTemplates.length > 0}
      <div class="space-y-4">
        <h2 class="text-2xl font-semibold">Available Tasks</h2>
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {#each availableTaskTemplates as taskTemplate (taskTemplate.id)}
            <AvailableTaskTemplateCard
              {taskTemplate}
              onClaim={() => handleClaim(taskTemplate)}
            />
          {/each}
        </div>
      </div>
    {/if}

    <div class="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Earned This Week</CardTitle>
          <CardDescription>Amount earned this week</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="flex items-center gap-2">
            <DollarSign class="h-5 w-5" />
            <span class="text-2xl font-bold"
              >{wageSummary.earnedThisWeek.toFixed(2)}</span
            >
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recently Paid</CardTitle>
          <CardDescription>Amount paid in last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="flex items-center gap-2">
            <DollarSign class="h-5 w-5" />
            <span class="text-2xl font-bold"
              >{wageSummary.paidLast30Days.toFixed(2)}</span
            >
          </div>
        </CardContent>
      </Card>
    </div>

    {#if recentTasks.length > 0}
      <div class="space-y-4">
        <h2 class="text-2xl font-semibold">Recent Completions</h2>
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {#each recentTasks as task (task.id)}
            <TaskCard
              taskInstance={task}
              showActions={false}
              compactDate={true}
            />
          {/each}
        </div>
      </div>
    {/if}

    <div class="flex justify-end">
      <Button href={userDashboardTaskHistoryPath()} variant="outline">
        View Full History
      </Button>
    </div>

    <ConfirmationDialog
      bind:open={completeDialogOpen}
      title="Complete Task"
      description="Are you sure you want to complete this task?"
      onConfirm={confirmComplete}
    />

    <ConfirmationDialog
      bind:open={claimDialogOpen}
      title="Claim Task"
      description="Are you sure you want to claim and complete this task?"
      onConfirm={confirmClaim}
    />

    <ConfirmationDialog
      bind:open={cancelDialogOpen}
      title="Cancel Task"
      description="Are you sure you want to cancel this task? This will remove it from your list."
      variant="destructive"
      confirmLabel="Cancel Task"
      onConfirm={confirmCancel}
    />
  </div>
</AppLayout>
